import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { 
  Loader2,
  Mail,
  Send,
  CheckCircle,
  XCircle,
  Clock,
  Bell,
  RefreshCw,
} from 'lucide-react';
import { toast } from 'sonner';
import Sidebar from '../components/Sidebar';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function NotificationsPage() {
  const { user, isSuperAdmin } = useAuth();
  const navigate = useNavigate();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sendingReminders, setSendingReminders] = useState(false);
  const [emailStatus, setEmailStatus] = useState({ configured: false, mode: 'mock', sender_email: null });

  useEffect(() => {
    fetchLogs();
    fetchEmailStatus();
  }, []);

  const fetchEmailStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/notifications/email-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEmailStatus(response.data);
    } catch (error) {
      // Default to mock mode if status check fails
      setEmailStatus({ configured: false, mode: 'mock', sender_email: null });
    }
  };

  const fetchLogs = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/notifications/email-logs?limit=50`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLogs(response.data.logs || []);
    } catch (error) {
      toast.error('Failed to load email logs');
    } finally {
      setLoading(false);
    }
  };

  const handleSendReminders = async () => {
    setSendingReminders(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_URL}/api/notifications/biometrics-reminders`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Sent ${response.data.reminders_sent} biometrics reminders`);
      fetchLogs(); // Refresh logs
    } catch (error) {
      toast.error('Failed to send reminders');
    } finally {
      setSendingReminders(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'sent':
        return <CheckCircle className="w-4 h-4 text-success" />;
      case 'failed':
        return <XCircle className="w-4 h-4 text-error" />;
      case 'mocked':
        return <Clock className="w-4 h-4 text-warning" />;
      default:
        return <Mail className="w-4 h-4 text-muted-foreground" />;
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'sent':
        return 'bg-success/10 text-success';
      case 'failed':
        return 'bg-error/10 text-error';
      case 'mocked':
        return 'bg-warning/10 text-warning';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Sidebar />

      {/* Main Content */}
      <main className="ml-0 md:ml-64 p-4 md:p-8 pt-16 md:pt-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-heading text-2xl font-bold text-foreground tracking-tight">
              Email Notifications
            </h1>
            <p className="font-body text-muted-foreground">
              View sent notifications and send appointment reminders
            </p>
          </div>
          <div className="flex gap-3">
            <Button 
              variant="outline" 
              className="font-body"
              onClick={fetchLogs}
              data-testid="refresh-logs-btn"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
            <Button 
              className="btn-lift bg-primary text-white font-body"
              onClick={handleSendReminders}
              disabled={sendingReminders}
              data-testid="send-reminders-btn"
            >
              {sendingReminders ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Send Biometrics Reminders
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Mail className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <p className="font-body text-2xl font-bold text-foreground">{logs.length}</p>
                  <p className="font-body text-xs text-muted-foreground">Total Emails</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-success/10 flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-success" />
                </div>
                <div>
                  <p className="font-body text-2xl font-bold text-success">{logs.filter(l => l.status === 'sent').length}</p>
                  <p className="font-body text-xs text-muted-foreground">Sent</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-warning/10 flex items-center justify-center">
                  <Clock className="w-5 h-5 text-warning" />
                </div>
                <div>
                  <p className="font-body text-2xl font-bold text-warning">{logs.filter(l => l.status === 'mocked').length}</p>
                  <p className="font-body text-xs text-muted-foreground">Mocked (Demo)</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-error/10 flex items-center justify-center">
                  <XCircle className="w-5 h-5 text-error" />
                </div>
                <div>
                  <p className="font-body text-2xl font-bold text-error">{logs.filter(l => l.status === 'failed').length}</p>
                  <p className="font-body text-xs text-muted-foreground">Failed</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Info Banner */}
        <Card className={`mb-6 ${emailStatus.configured ? 'bg-success/5 border-success/20' : 'bg-accent/5 border-accent/20'}`}>
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              {emailStatus.configured ? (
                <CheckCircle className="w-5 h-5 text-success mt-0.5" />
              ) : (
                <Bell className="w-5 h-5 text-accent mt-0.5" />
              )}
              <div>
                <h4 className="font-heading text-sm font-semibold text-foreground">Email Service Status</h4>
                {emailStatus.configured ? (
                  <p className="font-body text-xs text-muted-foreground mt-1">
                    Email service is <strong className="text-success">LIVE</strong>. Emails are being sent from <strong>{emailStatus.sender_email}</strong>.
                    All email activity is logged below.
                  </p>
                ) : (
                  <p className="font-body text-xs text-muted-foreground mt-1">
                    Emails are currently in <strong>MOCK mode</strong>. To enable real email sending, add a valid Resend API key to the backend environment variables.
                    All email content is being logged for review.
                  </p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Email Logs */}
        <Card data-testid="email-logs-card">
          <CardHeader>
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <Mail className="w-5 h-5 text-primary" />
              Email Log
            </CardTitle>
            <CardDescription className="font-body">
              Recent email notifications sent to clients
            </CardDescription>
          </CardHeader>
          <CardContent>
            {logs.length === 0 ? (
              <div className="text-center py-12">
                <Mail className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                <p className="font-body text-muted-foreground">No emails sent yet</p>
              </div>
            ) : (
              <div className="space-y-3">
                {logs.map((log) => (
                  <div 
                    key={log.id} 
                    className="p-4 rounded-lg border border-border hover:bg-secondary/30 transition-colors"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex items-start gap-3 flex-1">
                        {getStatusIcon(log.status)}
                        <div className="flex-1 min-w-0">
                          <p className="font-body font-medium text-foreground truncate">{log.subject}</p>
                          <p className="font-body text-sm text-muted-foreground mt-1">
                            To: {log.to}
                          </p>
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-1">
                        <span className={`px-2 py-1 rounded-full text-xs font-body font-medium ${getStatusBadge(log.status)}`}>
                          {log.status}
                        </span>
                        <span className="font-body text-xs text-muted-foreground">
                          {formatDate(log.created_at)}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
